// LM Audit d'acquisition — position des signatures DocuSign selon les lignes
// cochées au §2.2 (le nombre de pages varie de 5 à 12).
//
// Calibré sur le modèle Word actuel (public/LM Audit d'acquisition.docx) :
// 157 documents générés puis convertis en PDF et mesurés (pagination exacte),
// recalé sur le test réel « tout coché » = page 11, y 300.
//   1. On simule la pagination du §2.2 ligne imprimée par ligne imprimée.
//   2. La fin de la lettre (honoraires, clauses, signature) se place ensuite
//      toujours de la même façon selon la hauteur où finit le §2.2 : tables
//      mesurées FIN_LETTRE (différentes en honoraires fixes / proportionnels).
//   3. La signature est posée sous « Le Représentant légal, ».
// ⚠️ Toute modification de mise en page du Word (texte, polices, marges,
// paragraphes avant la signature, libellés du §2.2) impose de re-mesurer.
import { AUDIT_AXES, cleLigne, lignesExclues } from "./auditAcquisitionAxes";

// Nombre de lignes imprimées de chaque paragraphe du §2.2, dans l'ordre
// (titre puis ses lignes), mesuré dans le PDF.
const LIGNES_IMPRIMEES =
  "122111111111121111111111111112111121111121111211111111112111111111111111111111111111111111121111111111111111111121111111111122111111111111111111212121111111111111121121111111111111112121111111211211211111111111112112111";

const HAUT = 81.6; // première ligne de texte d'une page
const BAS = 773.5; // au-delà, la ligne passe à la page suivante
const PAS = 19.6; // d'un paragraphe au suivant
const INTERLIGNE = 13.65; // ligne suivante d'un même paragraphe
const DEBUT = { page: 2, y: 573.9 }; // « … et consiste à : »

// [y de fin du §2.2, pages ajoutées, y de « Le Représentant légal, »]
const FIN_LETTRE = {
  fixe: [
    [81.6, 1, 703.3], [101.2, 1, 717], [120.9, 2, 136.1], [140.4, 2, 136.1],
    [160.1, 2, 136.1], [173.7, 2, 136.1], [193.3, 2, 136.1], [212.9, 2, 136.1],
    [232.6, 2, 175.3], [252.1, 2, 175.3], [271.8, 2, 175.3], [291.4, 2, 208.6],
    [311.1, 2, 228.1], [330.7, 2, 255.4], [350.3, 2, 269.1], [369.9, 2, 288.7],
    [389.6, 2, 344.8], [403.1, 2, 344.8], [409.1, 2, 344.8], [428.8, 2, 344.8],
    [442.4, 2, 344.8], [448.4, 2, 382], [462.1, 2, 395.7], [468.1, 2, 395.7],
    [481.6, 2, 415.2], [487.6, 2, 415.2], [501.2, 2, 428.9], [520.9, 2, 428.9],
    [540.4, 2, 475.7], [560.1, 2, 509], [573.9, 2, 509], [579.7, 2, 509],
    [626.9, 2, 542.2], [632.6, 2, 559.8], [652.2, 2, 581.2], [660.1, 2, 581.2],
    [671.9, 2, 594.8], [679.7, 2, 608.5], [691.4, 2, 608.5], [699.3, 2, 629.4],
    [711.1, 2, 643.1], [718.9, 2, 643.1], [730.7, 2, 649.8], [738.6, 2, 663.9],
    [758.1, 2, 691.2], [763.9, 2, 691.2],
  ],
  proportionnel: [
    [81.6, 1, 691.8], [101.2, 1, 705.5], [120.9, 2, 136.1], [140.4, 2, 136.1],
    [160.1, 2, 136.1], [173.7, 2, 136.1], [193.3, 2, 136.1], [212.9, 2, 136.1],
    [232.6, 2, 175.3], [252.1, 2, 175.3], [271.8, 2, 175.3], [291.4, 2, 208.6],
    [311.1, 2, 208.6], [330.7, 2, 241.8], [350.3, 2, 269.1], [369.9, 2, 288.7],
    [389.6, 2, 308.3], [403.1, 2, 344.8], [409.1, 2, 344.8], [428.8, 2, 344.8],
    [442.4, 2, 344.8], [448.4, 2, 368.4], [462.1, 2, 382], [468.1, 2, 382],
    [481.6, 2, 395.7], [487.6, 2, 395.7], [501.2, 2, 415.2], [520.9, 2, 428.9],
    [540.4, 2, 462.2], [560.1, 2, 489.4], [573.9, 2, 489.4], [579.7, 2, 489.4],
    [626.9, 2, 522.7], [632.6, 2, 542.2], [652.2, 2, 569.7], [660.1, 2, 569.7],
    [671.9, 2, 583.3], [679.7, 2, 596.9], [691.4, 2, 596.9], [699.3, 2, 617.9],
    [711.1, 2, 631.5], [718.9, 2, 631.5], [730.7, 2, 638.3], [738.6, 2, 652.4],
    [758.1, 2, 679.7], [763.9, 2, 679.7],
  ],
};

// Recalage sur le test réel (tout coché : « Le Représentant légal, » mesuré
// à y 228,1 → signature validée à y 300), plafonné à y 725. Le bloc signature
// du Word est solidaire (« Paragraphes / Lignes solidaires » + 3 lignes vides
// réservées) : « Le Représentant légal, » ne descend jamais sous y 717, donc
// le plafond ne fait jamais chevaucher la signature sur du texte.
const DECALAGE_SIGNATURE = 72;
const Y_MAX = 725;

const PARAGRAPHES = (() => {
  const out = [];
  let k = 0;
  AUDIT_AXES.forEach((axe, i) => {
    out.push({ axe: i, n: +LIGNES_IMPRIMEES[k++] });
    axe.lignes.forEach((_, j) => out.push({ axe: i, cle: cleLigne(i, j), n: +LIGNES_IMPRIMEES[k++] }));
  });
  return out;
})();

function finParagraphe22(exclues) {
  const set = new Set(exclues);
  const axesGardes = new Set(PARAGRAPHES.filter((p) => p.cle && !set.has(p.cle)).map((p) => p.axe));
  let { page, y } = DEBUT;
  for (const p of PARAGRAPHES) {
    if (p.cle ? set.has(p.cle) : !axesGardes.has(p.axe)) continue;
    for (let l = 0; l < p.n; l++) {
      y += l === 0 ? PAS : INTERLIGNE;
      if (y > BAS) {
        page += 1;
        y = HAUT;
      }
    }
  }
  return { page, y };
}

export function positionSignatureAudit(formData) {
  const fin = finParagraphe22(lignesExclues(formData.audit_lignes_exclues));
  const table = FIN_LETTRE[formData.type_remuneration === "proportionnel" ? "proportionnel" : "fixe"];
  const [, pagesAjoutees, yRepresentant] = table.reduce((a, e) => (Math.abs(e[0] - fin.y) < Math.abs(a[0] - fin.y) ? e : a));
  return {
    pageNumber: fin.page + pagesAjoutees,
    yPosition: Math.round(Math.min(yRepresentant + DECALAGE_SIGNATURE, Y_MAX)),
  };
}

// La position voyage jusqu'à DocuSign dans nombreLignesTableau (déjà
// transmis à la signature directe ET à la validation comptable) :
// code = page × 1000 + y.
export const codePositionAudit = (formData) => {
  const { pageNumber, yPosition } = positionSignatureAudit(formData);
  return pageNumber * 1000 + yPosition;
};
export const decoderPositionAudit = (code, xPosition) => {
  const c = Number(code) || 0;
  return c >= 1000
    ? { pageNumber: Math.floor(c / 1000), xPosition, yPosition: c % 1000 }
    : { pageNumber: 11, xPosition, yPosition: 300 }; // repli : tout coché
};
